// 17/09 (#156) : quels scripts envoyés dans une conversation ont été PAYÉS, d'après les ventes du fan.
// Le fan paie souvent un peu PLUS que le prix du script (frais ajoutés par Dropp, vus sur les vraies ventes :
// 5 € → 5,20 · 8 € → 8,60 · 16 € → 17,30). L'ancienne règle exigeait le montant exact au centime : ces ventes-là
// n'étaient jamais reconnues et le script restait « ✗ pas payé ». On accepte donc une marge vers le HAUT, et en cas
// de plusieurs scripts possibles on garde ceux dont le prix est le plus proche du montant encaissé.
export const MARGE_FRAIS = 0.15;   // jusqu'à +15 %…
export const MARGE_MAX = 0.6;      // …et JAMAIS plus de 0,60 € : c'est le plus gros frais Dropp jamais constaté
export const MARGE_FIXE = 0.05;    // (+ 5 centimes d'arrondi)

// 18/09 (2e vérification indépendante) : avec un plafond à 1 €, 9 dons ronds passaient encore pour des paiements
// de script (20 € ← script 19 €, 100 € ← 99 €, 9 € ← 8 €…). Or les frais Dropp n'ont jamais dépassé +0,60 € :
// les seuls montants à centimes vus en prod sont 5,20 · 5,50 · 8,60 · 16,44 · 17,30. Le plafond à 0,60 € suffit
// à écarter ces dons, sans avoir à connaître le mode de paiement.
export function montantCorrespond(prix, montant) {
  if (!(prix > 0) || !(montant > 0)) return false;
  if (montant < prix - 0.01) return false;   // le fan a payé moins que le prix
  return montant <= prix + Math.min(prix * MARGE_FRAIS, MARGE_MAX) + MARGE_FIXE;
}

// prixParId : { scriptId: prix } · datesEnvoi : { scriptId: [Date, …] } · ventes : [{ amount, date, status }]
export function scriptsPayes({ dejaPayes = [], envoyes = [], prixParId = {}, datesEnvoi = {}, ventes = [] }) {
  const payes = new Set(dejaPayes);
  if (!envoyes.length || !ventes.length) return payes;
  ventes.filter(v => v.status === 'approved').forEach((vente) => {
    const montant = Number(vente.amount) || 0;
    const dateVente = new Date(vente.date);
    const possibles = envoyes.filter(id => montantCorrespond(prixParId[id] || 0, montant));
    if (!possibles.length) return;
    // Plusieurs prix possibles (ex. 5 € et 5,50 € pour un encaissement de 5,20 €) → on garde le plus proche.
    const ecart = id => Math.abs(montant - (prixParId[id] || 0));
    const mini = Math.min(...possibles.map(ecart));
    const candidats = possibles.filter(id => ecart(id) - mini < 0.01);
    // Entre deux scripts au même prix, celui envoyé le plus récemment AVANT le paiement.
    let meilleur = null;
    let meilleureDate = null;
    candidats.forEach((id) => {
      const dates = (datesEnvoi[id] || []).filter(d => new Date(d) <= dateVente);
      const derniere = dates.length ? new Date(Math.max(...dates.map(d => new Date(d).getTime()))) : null;
      if (derniere && (!meilleureDate || derniere > meilleureDate)) { meilleur = id; meilleureDate = derniere; }
    });
    if (meilleur) { payes.add(meilleur); return; }
    // Aucun candidat envoyé avant le paiement. On ne marque QUE ceux dont on ne connaît aucune date d'envoi :
    // un script dont on sait qu'il est parti APRÈS le paiement ne peut pas avoir été payé par cette vente.
    candidats.filter(id => !(datesEnvoi[id] || []).length).forEach(id => payes.add(id));
  });
  return payes;
}

// 18/09 (Marlon) : « après le bot, on voit le lien de paiement ». Le bot d'accueil a fait la découverte, envoyé la
// photo de bienvenue PUIS son lien de paiement. Le chatteur qui reprend le fan ne doit pas renvoyer la même chose :
// on grise ce que le bot a déjà fait, et si le fan a payé LE lien du bot, le PPV correspondant passe en ✓ PAYÉ tout seul.
const sansAccent = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// scripts : [{ id, title, name, price, link }] · ventes : [{ amount, status, reason }]
// Retourne { faits: [ids grisés « déjà fait par le bot »], payes: [ids à passer en ✓ PAYÉ] }
export function scriptsDuBot({ scripts = [], ventes = [], venuDuBot = false }) {
  // 18/09 : une vente encaissée SUR LE LIEN DU BOT prouve à elle seule que le fan est passé par lui — les drapeaux
  // du bot (zone_bot, bot_lien…) disparaissent quand le fan est rendu aux chatteurs (4 ventes sur 6 étaient dans ce cas).
  const ventesBot = ventes.filter(v => String(v.reason || '').startsWith('auto:bot_'));
  if (!venuDuBot && !ventesBot.length) return { faits: [], payes: [] };
  // 1) ce que le bot envoie avant le lien : les scripts « Bienvenue » gratuits (un pack payant « Bienvenue » reste à vendre)
  const faits = scripts.filter(s => sansAccent(s.title || s.name).includes('bienvenue')
    && !(Number(s.price) > 0) && !s.link).map(s => s.id);
  // 2) le PPV du bot : une vente encaissée SUR LE LIEN DU BOT désigne le script vendu à ce prix.
  const payes = [];
  ventesBot.filter(v => v.status === 'approved').forEach((vente) => {
    const montant = Number(vente.amount) || 0;
    const possibles = scripts.filter(s => montantCorrespond(Number(s.price) || 0, montant));
    if (!possibles.length) return;
    const ecart = s => Math.abs(montant - (Number(s.price) || 0));
    const mini = Math.min(...possibles.map(ecart));
    let candidats = possibles.filter(s => ecart(s) - mini < 0.01);
    // Plusieurs scripts au même prix : on ne devine pas, sauf si un seul est un PPV (le funnel du bot).
    if (candidats.length > 1) {
      const ppv = candidats.filter(s => sansAccent(s.title || s.name).includes('ppv'));
      if (ppv.length !== 1) return;
      candidats = ppv;
    }
    if (!payes.includes(candidats[0].id)) payes.push(candidats[0].id);
  });
  return { faits: [...faits, ...payes], payes };
}
